/**
 * Remote control of the signed-in developer's own agents.
 *
 * The developer leaves an agent running and goes out. When the agent finishes a turn, a hook on
 * their machine posts what it said and waits for the next instruction; this page shows that reply
 * and sends the instruction. Built for a phone: one column, the thread, a box to type in.
 *
 * Nobody else's agents appear here and there is no way to address them: the server takes whose
 * agents these are from who is signed in, never from the request.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Monitor, Power, RadioTower, Send, ShieldCheck, X, Star } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { ConfirmIdentity } from '../components/ConfirmIdentity'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { formatRelative, formatTime } from '../lib/format'
import { readableReply } from '../lib/agentReply'
import { Badge, CodeBlock, EmptyState, ErrorBanner, Panel, Skeleton } from '../components/primitives'
import type { RemoteAgent, RemoteMessage, RemoteOverview } from '../lib/types'
import { TeamWorkSwitch } from '../components/TeamWorkSwitch'
import { composeFor } from '../lib/dockPlacement'

/** The longest command the server takes. Kept in step with RemoteControl.MaxCommandLength. */
const MAX_COMMAND = 4000


/** Errors the server explains with a code; each has its own message in both languages. */
const KNOWN_ERRORS = new Set(['remote_off', 'remote_not_allowed', 'too_many_pending', 'too_long', 'required'])

const HOOK_SETTINGS = `{
  "hooks": {
    "Stop": [
      { "hooks": [ { "type": "command", "command": "node /path/to/devpulse/mcp/hooks/remote-control.mjs", "timeout": 1860 } ] }
    ]
  }
}`

export default function Remote() {
  const { t } = useI18n()
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })

  if (isLoading) return <Skeleton height={160} />
  if (!me?.developerId) {
    return (
      <Panel>
        <EmptyState title={t('remote.noDeveloper')} body={t('remote.noDeveloperHint')} />
      </Panel>
    )
  }

  return <RemoteControl />
}

function RemoteControl() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const canManage = atLeast(getSession()?.role, 'EngineeringManager')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['remote'],
    queryFn: api.remote.overview,
    // While it is on, the reply is what the developer is waiting for: ask often. Off, rarely.
    refetchInterval: (query) => (query.state.data?.enabled ? 4_000 : 30_000),
  })

  const [failure, setFailure] = useState<string | null>(null)
  const explain = (err: unknown) => {
    if (err instanceof ApiError && err.code && KNOWN_ERRORS.has(err.code)) return t(`remote.error.${err.code}`)
    if (err instanceof ApiError) return t(err.translationKey)
    return t('error.generic')
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['remote'] })
  const onError = (err: unknown) => setFailure(explain(err))

  // Switching on asks for a recent sign-in. When the server says so, the form to confirm it
  // appears, and the switch is tried again with the fresh token.
  const [confirming, setConfirming] = useState<number | null>(null)

  const toggle = useMutation({
    mutationFn: ({ enabled, hours }: { enabled: boolean; hours?: number }) => api.remote.setEnabled(enabled, hours),
    onSuccess: () => { setFailure(null); setConfirming(null); void refresh() },
    onError: (err, variables) => {
      if (err instanceof ApiError && err.code === 'reauth_required' && variables.enabled) {
        setFailure(null)
        setConfirming(1)
        return
      }
      onError(err)
    },
  })

  const send = useMutation({
    mutationFn: ({ text, target }: { text: string; target: string | null }) => api.remote.send(text, target),
    onSuccess: () => { setFailure(null); void refresh() },
    onError,
  })

  const cancel = useMutation({
    mutationFn: (id: string) => api.remote.cancel(id),
    onSuccess: () => void refresh(),
    onError,
  })

  if (isLoading) return <Skeleton height={240} />
  if (error || !data) {
    return <ErrorBanner message={explain(error)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
  }

  return (
    <div className="remote stack">
      {failure && <ErrorBanner message={failure} />}

      <MachinesPanel />

      <SwitchPanel
        overview={data}
        canManage={canManage}
        busy={toggle.isPending}
        onEnable={() => toggle.mutate({ enabled: true })}
        onDisable={() => toggle.mutate({ enabled: false })}
      />

      {confirming !== null && (
        <ConfirmIdentity
          onConfirmed={() => toggle.mutate({ enabled: true })}
          onCancel={() => setConfirming(null)}
        />
      )}

      {data.allowed && (
        <>
          <TeamWorkSwitch accepting={data.acceptTeamWork ?? false} />
          <AgentsPanel agents={data.agents} />

          <Panel title={t('remote.thread')} subtitle={t('remote.threadHint')}>
            <Thread
              messages={data.messages}
              agents={data.agents}
              onCancel={(id) => cancel.mutate(id)}
              cancelling={cancel.isPending ? cancel.variables : undefined}
            />
            <Composer
              enabled={data.enabled}
              agents={data.agents}
              sending={send.isPending}
              onSend={(text, target) => send.mutateAsync({ text, target }).then(() => true, () => false)}
            />
          </Panel>

          <SetupPanel developerName={data.developerName ?? null} ownKeys={data.ownKeys ?? 0} open={data.agents.length === 0 || (data.ownKeys ?? 0) === 0} />
        </>
      )}

      <p className="remote__privacy tiny muted">
        <ShieldCheck size={13} aria-hidden /> {t('remote.privacy')}
      </p>
    </div>
  )
}

function SwitchPanel({
  overview,
  canManage,
  busy,
  onEnable,
  onDisable,
}: {
  overview: RemoteOverview
  canManage: boolean
  busy: boolean
  onEnable: () => void
  onDisable: () => void
}) {
  const { t } = useI18n()

  if (!overview.allowed) {
    return (
      <Panel title={<span className="row row--tight"><RadioTower size={16} aria-hidden /> {t('remote.title')}</span>}>
        <div className="stack stack--tight">
          <span className="small">{t('remote.notAllowed')}</span>
          <span className="tiny muted">
            {canManage ? <Link to="/settings/workflow">{t('remote.notAllowedManager')}</Link> : t('remote.notAllowedAsk')}
          </span>
        </div>
      </Panel>
    )
  }

  return (
    <Panel
      title={<span className="row row--tight"><RadioTower size={16} aria-hidden /> {t('remote.title')}</span>}
      subtitle={t('remote.subtitle')}
    >
      {overview.enabled ? (
        <div className="row row--between row--wrap">
          <span className="row row--tight">
            <Badge tone="ok">{t('remote.on')}</Badge>
            {/* No time limit (docs/refactor/28): on until the person switches it off. */}
            <span className="small muted">{t('remote.noLimit')}</span>
          </span>
          <button type="button" className="btn btn--danger btn--sm" onClick={onDisable} disabled={busy}>
            <Power size={13} aria-hidden /> {t('remote.switchOff')}
          </button>
        </div>
      ) : (
        <div className="stack stack--tight">
          <span className="small">{t('remote.offHint')}</span>
          <div className="row row--wrap">
            <button type="button" className="btn btn--primary btn--sm" onClick={() => onEnable()} disabled={busy}>
              <Power size={13} aria-hidden /> {t('remote.switchOn')}
            </button>
          </div>
        </div>
      )}
    </Panel>
  )
}

/**
 * The person's computers with the desktop app (docs/refactor/10-paridade-web-celular.md): which are on,
 * the projects open on each, what each is running — and a way to send work to one of them, which puts
 * "@its-name" in the prompt box.
 */
function MachinesPanel() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { data: machines, isLoading } = useQuery({ queryKey: ['machines'], queryFn: api.promptQueue.machines, refetchInterval: 15_000, retry: false })
  // The principal computer (docs/refactor/23): a task with several agents sent from elsewhere is orchestrated there.
  const principal = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => api.promptQueue.principal(id, on),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['machines'] }),
  })
  const self = (() => {
    try {
      return localStorage.getItem('devpulse.machineId')
    } catch {
      return null
    }
  })()

  return (
    <Panel title={<span className="row row--tight"><Monitor size={16} aria-hidden /> {t('machines.title')}</span>} subtitle={t('machines.hint')}>
      {isLoading ? (
        <Skeleton height={60} />
      ) : !machines?.length ? (
        <EmptyState title={t('machines.none')} body={t('machines.noneHint')} />
      ) : (
        <ul className="machines">
          {machines.map((machine) => {
            const name = machine.name ?? t('machines.unnamed')
            return (
              <li key={machine.id} className={`machines__row${machine.online ? ' is-on' : ''}`}>
                <div className="row row--tight row--wrap">
                  <span className={`machines__dot${machine.online ? ' is-on' : ''}`} aria-hidden />
                  <strong className="small">{name}</strong>
                  {machine.id === self && <Badge tone="accent">{t('machines.thisOne')}</Badge>}
                  <button
                    type="button"
                    className={`btn btn--ghost btn--icon btn--sm machines__star${machine.principal ? ' is-on' : ''}`}
                    aria-pressed={Boolean(machine.principal)}
                    aria-label={machine.principal ? t('machines.principalOff', { name }) : t('machines.principalOn', { name })}
                    title={machine.principal ? t('machines.principalIs') : t('machines.principalHint')}
                    disabled={principal.isPending}
                    onClick={() => principal.mutate({ id: machine.id, on: !machine.principal })}
                  >
                    <Star size={14} aria-hidden fill={machine.principal ? 'currentColor' : 'none'} />
                  </button>
                  {machine.principal && <span className="tiny machines__principal">{t('machines.principal')}</span>}
                  <span className="tiny muted">{machine.online ? t('machines.on') : t('machines.seen', { when: formatRelative(machine.lastSeenAt, locale) })}</span>
                  <span className="spacer" />
                  {machine.online && machine.id !== self && machine.name && (
                    <button type="button" className="btn btn--sm" onClick={() => composeFor(machine.name!)}>
                      <Send size={13} aria-hidden /> {t('machines.send')}
                    </button>
                  )}
                </div>
                {machine.repositories.length > 0 && (
                  <div className="row row--tight row--wrap tiny muted">
                    {machine.repositories.map((repository) => <span key={repository} className="mono machines__repo">{repository}</span>)}
                  </div>
                )}
                {/* What its agent may do there without asking (docs/refactor/59): changed on that computer, in Conectar agente. */}
                {machine.permission && <div className="tiny muted" title={t('machines.canHint')}>{t(`machines.can.${machine.permission}`)}</div>}
                {machine.running.map((item) => (
                  <div key={item.id} className="row row--tight tiny machines__running">
                    <Badge tone="info">{t('dock.queue.Running')}</Badge>
                    <span className="truncate">{item.prompt.split(/\r?\n/)[0]}</span>
                    {item.workSessionId && <Link to={`/work/${item.workSessionId}`} className="nowrap">{t('dock.watchLive')}</Link>}
                  </div>
                ))}
              </li>
            )
          })}
        </ul>
      )}
    </Panel>
  )
}

function agentName(agent: RemoteAgent | undefined, clientSessionId: string | null | undefined): string {
  const repository = agent?.repository ?? null
  const id = (clientSessionId ?? agent?.clientSessionId ?? '').slice(0, 8)
  return repository ? `${repository} · ${id}` : id
}

function AgentsPanel({ agents }: { agents: RemoteAgent[] }) {
  const { t, locale } = useI18n()

  return (
    <Panel title={t('remote.agents')} subtitle={t('remote.agentsHint')}>
      {agents.length === 0 ? (
        <span className="small muted">{t('remote.noAgents')}</span>
      ) : (
        <ul className="remote__agents">
          {agents.map((agent) => (
            <li key={agent.clientSessionId} className="remote__agent">
              <Bot size={15} aria-hidden />
              <span className="remote__agent-name">{agentName(agent, agent.clientSessionId)}</span>
              {agent.working ? (
                <Badge tone="accent">{t('remote.agentWorking')}</Badge>
              ) : agent.waiting ? (
                <Badge tone="ok">{t('remote.agentWaiting')}</Badge>
              ) : (
                <span className="tiny muted">{t('remote.agentSeen', { when: formatRelative(agent.lastPolledAt, locale) })}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

function Thread({
  messages,
  agents,
  onCancel,
  cancelling,
}: {
  messages: RemoteMessage[]
  agents: RemoteAgent[]
  onCancel: (id: string) => void
  cancelling: string | undefined
}) {
  const { t, locale } = useI18n()
  const end = useRef<HTMLDivElement>(null)
  const byId = useMemo(() => new Map(agents.map((a) => [a.clientSessionId, a])), [agents])

  // Keep the newest message in view as replies arrive, the way a chat does.
  const last = messages.at(-1)?.id
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'nearest' })
  }, [last])

  // What the agent wrote, with no internal status line and the app's English said in the person's language (41, A9).
  const agentSaid = (text: string) => readableReply(text, t)

  if (messages.length === 0) {
    return <p className="small muted remote__empty">{t('remote.empty')}</p>
  }

  return (
    <div className="remote__thread" role="log" aria-live="polite">
      {messages.map((message) => {
        const mine = message.direction === 'ToAgent'
        const target = message.clientSessionId ? agentName(byId.get(message.clientSessionId), message.clientSessionId) : null

        return (
          <div key={message.id} className={`remote__bubble ${mine ? 'remote__bubble--mine' : 'remote__bubble--agent'}`}>
            <div className="remote__meta tiny muted">
              {mine ? t('remote.you') : t('remote.agent')}
              {target && <> · {mine ? t('remote.to', { agent: target }) : target}</>}
              {' · '}
              {formatTime(message.createdAt, locale)}
            </div>
            <div className="remote__text">{mine ? message.text : agentSaid(message.text).body}</div>
            {!mine && agentSaid(message.text).pending && (
              <div className="tiny remote__needs">{t('dock.needsYou', { reason: agentSaid(message.text).pending! })}</div>
            )}
            {mine && (
              <div className="row row--tight remote__status">
                <StatusBadge message={message} />
                {message.status === 'Pending' && !agents.some((agent) => agent.waiting) && (
                  <span className="tiny muted remote__nobody">{t('remote.nobodyWaiting')}</span>
                )}
                {message.status === 'Pending' && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => onCancel(message.id)}
                    disabled={cancelling === message.id}
                  >
                    <X size={12} aria-hidden /> {t('remote.cancel')}
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
      <div ref={end} />
    </div>
  )
}

function StatusBadge({ message }: { message: RemoteMessage }) {
  const { t, locale } = useI18n()

  switch (message.status) {
    case 'Pending':
      return <Badge tone="warn">{t('remote.status.Pending')}</Badge>
    case 'Delivered':
      return (
        <Badge tone="ok">
          {message.deliveredAt != null
            ? t('remote.status.DeliveredAt', { time: formatTime(message.deliveredAt, locale) })
            : t('remote.status.Delivered')}
        </Badge>
      )
    case 'Expired':
      return <Badge tone="danger">{t('remote.status.Expired')}</Badge>
    case 'Cancelled':
      return <Badge>{t('remote.status.Cancelled')}</Badge>
    default:
      return null
  }
}

function Composer({
  enabled,
  agents,
  sending,
  onSend,
}: {
  enabled: boolean
  agents: RemoteAgent[]
  sending: boolean
  onSend: (text: string, target: string | null) => Promise<boolean>
}) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [target, setTarget] = useState('')

  // A target that has gone away falls back to "any agent" rather than addressing nobody.
  const known = agents.some((a) => a.clientSessionId === target)
  const effectiveTarget = known ? target : ''

  const trimmed = text.trim()
  const canSend = enabled && !sending && trimmed.length > 0 && trimmed.length <= MAX_COMMAND

  const submit = async () => {
    if (!canSend) return
    if (await onSend(trimmed, effectiveTarget || null)) setText('')
  }

  return (
    <form
      className="remote__composer stack stack--tight"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <textarea
        className="textarea"
        rows={3}
        value={text}
        maxLength={MAX_COMMAND}
        disabled={!enabled}
        placeholder={enabled ? t('remote.placeholder') : t('remote.placeholderOff')}
        aria-label={t('remote.command')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends; Ctrl+Enter (and Shift+Enter) breaks the line (the owner's call, 16/10).
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) return
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void submit()
          }
        }}
      />
      <div className="row row--between row--wrap">
        {agents.length > 1 ? (
          <label className="row row--tight small">
            <span className="muted">{t('remote.target')}</span>
            <select className="select select--sm" value={effectiveTarget} onChange={(e) => setTarget(e.target.value)} disabled={!enabled}>
              <option value="">{t('remote.anyAgent')}</option>
              {agents.map((agent) => (
                <option key={agent.clientSessionId} value={agent.clientSessionId}>
                  {agentName(agent, agent.clientSessionId)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="tiny muted">{t('remote.sendHint')}</span>
        )}
        <span className="row row--tight">
          <span className="tiny muted">{trimmed.length}/{MAX_COMMAND}</span>
          <button type="submit" className="btn btn--primary btn--sm" disabled={!canSend}>
            <Send size={13} aria-hidden /> {sending ? t('remote.sending') : t('remote.send')}
          </button>
        </span>
      </div>
    </form>
  )
}

function SetupPanel({ developerName, ownKeys, open }: { developerName: string | null; ownKeys: number; open: boolean }) {
  const { t } = useI18n()

  // Open by itself while no agent has ever answered: that is when these steps are the answer.
  return (
    <details className="remote__setup panel" open={open}>
      <summary className="panel__header">
        <h2 className="panel__title">{t('remote.setup')}</h2>
      </summary>
      <div className="panel__body stack stack--tight">
        <ol className="remote__steps small">
          <li>{t('remote.setup.hook')}</li>
          <li>{developerName ? t('remote.setup.keyFor', { name: developerName }) : t('remote.setup.key')}</li>
          <li>{t('remote.setup.restart')}</li>
          <li>{t('remote.setup.switch')}</li>
        </ol>
        <CodeBlock code={HOOK_SETTINGS} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
        <span className="tiny muted">{t('remote.setup.keyboard')}</span>
        <p className="small" style={{ margin: 0 }}>
          {ownKeys > 0 ? t('remote.keys.have', { count: ownKeys }) : t('remote.keys.none')}{' '}
          <Link to="/agents/connect?tab=keys">{t('remote.keys.manage')}</Link>
        </p>
      </div>
    </details>
  )
}
